import { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { Form } from "antd";
import dayjs from "dayjs";
import CommonDatePicker from "@/common/elements/DatePicker";
import Checkbox from "@/common/elements/InputElements/CheckBox";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import { StyledGridCol, StyledGridRow } from "@/common/styles";
import { BookingsSectionTitle } from "../styles";
import { getOptionsFromRefList, getRefListNameByID } from "@/util/common_function";
import notificationService from "@/util/notificationService";
import { useSelector } from "react-redux";

const BookingQueueDetailForm = forwardRef(
  (
    {
      stepData,
      bookings,
      existingSteps,
      isEditing,
      onCloseModal,
      queueType = "bookingQueue",
    },
    ref
  ) => {
    const [form] = Form.useForm();
    const userDetails = JSON.parse(localStorage.getItem("userDetails"));
    const [stepOptions, setStepOptions] = useState(
      getOptionsFromRefList(
        queueType === "releaseQueue" ? "ReleaseQueueStep" : "BookingQueueStep",
        false,
        false,
        true
      )
    );
console.log({stepData},"step")
    // Determine the appropriate step key based on queue type
    const stepKey =
      queueType === "releaseQueue" ? "releaseQueStepID" : "queStepID";
    const detailIDKey =
      queueType === "releaseQueue"
        ? "releaseQueueDetailID"
        : "bookingQueueDetailID";

    const { manualBookingData } = useSelector((state) => state.bookings);

    const [queueDetail, setQueueDetail] = useState({
      [detailIDKey]: stepData?.[detailIDKey] || null,
      [stepKey]: stepData?.value || "",
      completionDateTime:
        stepData?.value && !stepData?.[detailIDKey] ? dayjs() : "",
      completionStaffID: stepData?.value ? userDetails?.staffId : "",
      comments: "",
      complete: stepData?.value ? true : false,
      dateEnteredAudit:stepData?.dateAdded,
      timeEnteredAudit:stepData?.timeAdded,
      idAudit:detailIDKey=="bookingQueueDetailID"?stepData?.bookingQueueDetailID:stepData?.releaseQueueDetailID,
creatorStaff:stepData?.creatorStaffID?getRefListNameByID("",stepData.creatorStaffID,true):""
    });

    useEffect(() => {
      if (manualBookingData.isManualBooking) {
        const data = manualBookingData?.value;
        const list = getOptionsFromRefList(
          queueType === "releaseQueue"
            ? "ReleaseQueueStep"
            : "BookingQueueStep",
          false,
          false,
          true
        );

        const manualStep = list?.find((ms) => ms.label.includes(data));

        setStepOptions(manualStep ? [manualStep] : []);
      }
    }, []);
    // Expose methods to the parent component
    useImperativeHandle(ref, () => ({
      getFieldsValue: async () => {
        const formValues = await form.validateFields();
        const { completionDateTime } = formValues;

        // Check for duplicate step only if not editing
        if (!isEditing) {
          const isDuplicate = existingSteps?.some(
            (stepID) => stepID === formValues[stepKey]
          );
          if (isDuplicate) {
            notificationService.warning(
              "This step is already added. Please select a different step."
            );
            onCloseModal(); // Close the modal on warning
            throw new Error("Duplicate StepID");
          }
        } else {
          // Editing mode: Exclude the current step from duplicate check
          const isDuplicate = existingSteps?.some(
            (stepID) =>
              stepID === formValues[stepKey] && stepID !== stepData[stepKey]
          );
          if (isDuplicate) {
            notificationService.warning(
              "This step is already added. Please select a different step."
            );
            onCloseModal(); // Close the modal on warning
            throw new Error("Duplicate StepID");
          }
        }

        // Split `completionDateTime` into `completionDate` and `completionTime`
        const formattedValues = {
          ...formValues,
          completionDate: completionDateTime
            ? dayjs(completionDateTime).format("MM/DD/YYYY")
            : null,
          completionTime: completionDateTime
            ? dayjs(completionDateTime).format("HH:mm")
            : null,
        };

        delete formattedValues.completionDateTime; // Remove the combined field
        return formattedValues;
      },
      resetFields: () => form.resetFields(),
    }));

    // Populate form when `stepData` changes
    useEffect(() => {
      if (stepData) {
        setQueueDetail({
          ...stepData,
          completionDateTime: stepData.completionDate
            ? dayjs(
                `${stepData.completionDate} ${stepData.completionTime || ""}`
              )
            : !stepData?.[detailIDKey]
            ? dayjs()
            : null,
        });
      }
    }, [stepData]);

    // Update form fields with state
    useEffect(() => {
      form.setFieldsValue(queueDetail);
    }, [queueDetail, form]);

    // When the "Complete" checkbox is toggled, fill or clear the fields accordingly.
    const handleCompleteChange = (e) => {
      const isComplete = e.target.checked;
      if (isComplete) {
        const now = dayjs(); // current date/time
        form.setFieldsValue({
          completionDateTime: now,
          completionStaffID: userDetails?.staffId,
        });
      } else {
        // When unchecked, clear the fields
        form.setFieldsValue({
          completionDateTime: "",
          completionStaffID: "",
        });
      }
    };

    // Allow editing of the date picker value
    const handleDateChange = (value) => {
      form.setFieldsValue({ completionDateTime: value });
    };

    return (
      <Form form={form} layout="vertical">
        <Form.Item name={detailIDKey} hidden>
          <input type="hidden" />
        </Form.Item>
        <StyledGridRow gutter={16}>
          <StyledGridCol xs={6}>
            <FormItem
              name="complete"
              label="Complete"
              layout="horizontal"
              labelPostion="label-right"
              valuePropName="checked"
            >
              <Checkbox onChange={handleCompleteChange} />
            </FormItem>
          </StyledGridCol>
        </StyledGridRow>
        <StyledGridRow gutter={16}>
          <StyledGridCol xs={6}>
            <FormItem
              name={stepKey}
              label="Step"
              rules={[{ required: true, message: "Please select a step" }]}
            >
              <Select
                disabled={isEditing}
                options={stepOptions}
                placeholder="Step"
                showSearch={false}
                variant="outlined"
              />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol xs={6}>
            <FormItem name="completionDateTime" label="Completion Date/Time">
              <CommonDatePicker
                placeholder="MM/DD/YYYY HH:mm"
                format="MM/DD/YYYY HH:mm"
                showTime={{ format: "HH:mm" }}
                onChange={handleDateChange}
                disabled={isEditing}
              />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol xs={12}>
            <FormItem name="completionStaffID" label="Completed By">
              <Select
                disabled={isEditing}
                options={getOptionsFromRefList("", true, false, true)}
                placeholder="Completed By"
                showSearch={false}
                variant="outlined"
              />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol xs={24}>
            <FormItem name="comments" label="Comments">
              <TextField inputType="textarea" disabled={isEditing}/>
            </FormItem>
          </StyledGridCol>
        </StyledGridRow>

        <BookingsSectionTitle>
          <span>
            {queueType === "releaseQueue"
              ? "Release Queue Details Administrative Information"
              : "Booking Queue Details Administrative Information"}
          </span>
        </BookingsSectionTitle>

        <StyledGridRow gutter={16}>
          <StyledGridCol xs={4}>
            <FormItem label="ID" name="idAudit">
              <TextField disabled />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol xs={4}>
            <FormItem label="Date Entered" name="dateEnteredAudit">
              <TextField disabled />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol xs={4}>
            <FormItem label="Time Entered" name="timeEnteredAudit">
              <TextField disabled />
            </FormItem>
          </StyledGridCol>
          <StyledGridCol xs={6}>
            <FormItem label="User" name="creatorStaff">
              <TextField disabled />
            </FormItem>
          </StyledGridCol>
        </StyledGridRow>
      </Form>
    );
  }
);

export default BookingQueueDetailForm;
